/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import previewLixi from '@assets/previewLixi.png';
import {
  Box,
  Flex,
  FormControl,
  Heading,
  Image,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { parseUnits } from '@ethersproject/units';
import {
  CreatingStage,
  resetEnvelopeForm,
  selectCurrentCreatingStage,
  selectCurrentLixiBagAddress,
  selectEnvelopeFormFields,
  selectEnvelopeFormLogoUri,
  setCreatingStage,
  setBlockhashHash,
  setCreatorWalletAddress,
  setFields,
} from '@features';
import { useAppDispatch, useAppSelector, useFormStep } from '@hooks';
import { ADDRESS_ZERO } from '@utils';
import {
  useApproveToken,
  useMintLixi,
  createCampaignViaChainManager,
  getTokenDecimalsViaChainManager,
} from '../../chain';
import { useChainManager } from '../../chain/hooks/useChainManager';
import { getChainConfig } from '../../config/chains';
import { SOLANA_PROGRAM_ID } from '@constants';
import React, { useEffect, useState, useCallback } from 'react';
import { EnvelopeCreatingModal } from '../EnvelopeCreatingModal';
import { EnvelopeWarningModal } from '../EnvelopeWarningModal';
import { CampaignTimeStatus } from '../CampaignTimeStatus';
import { Amount } from './Amount';
import { ExtraTokenForm } from './ExtraToken';
import { SolanaExtraTokenForm } from './SolanaExtraTokenForm';
import { FormTitle } from './FormTitle';
import { GeneralForm } from './GeneralForm';

export const EnvelopeForm: React.FC = () => {
  const dispatch = useAppDispatch();

  const fileUri = useAppSelector(selectEnvelopeFormLogoUri());
  const creatingStage = useAppSelector(selectCurrentCreatingStage);
  const fieldState = useAppSelector(selectEnvelopeFormFields());

  const { step, nextStep, goBack } = useFormStep(3);

  const [isCreateConfirmed, setIsCreateConfirmed] = useState(false);
  const [isCreateOnSCFired, setIsCreateOnSCFired] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const lixiBagAddress = useAppSelector(selectCurrentLixiBagAddress);

  const stage = useAppSelector(selectCurrentCreatingStage);

  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: 'bottom',
  });

  // Listen mint lixi event (unified hook)
  useMintLixi();

  // Get spender address based on current chain
  // For BNB: use lixiBagAddress (contract address)
  // For Solana: use Program ID, not contract address (Ethereum format)
  const { currentChain, wallet, chainManager } = useChainManager();
  const chainConfig = getChainConfig(currentChain as 'bnb' | 'solana');
  const spenderAddress =
    currentChain === 'solana'
      ? chainConfig.programId || SOLANA_PROGRAM_ID
      : lixiBagAddress;

  // Listen for token approval event (unified hook)
  const { isApproved, checkApproval, approve, isLoading: isApproving } = useApproveToken(
    fieldState.currency,
    spenderAddress,
    () => {
      // onApproved callback - will be handled by create campaign effect
    },
  );

  // // Clear redux when component unmount
  useEffect(() => {
    return () => {
      dispatch(resetEnvelopeForm());
    };
  }, []);

  useEffect(() => {
    (async () => {
      // Only proceed if user has confirmed payment by clicking "Thanh toán"
      // Stage will be CHECKING after user confirms, then WAITING_APPROVAL if approval is needed
      // For native tokens (SOL), approval check completes immediately and we proceed
      const hasUserConfirmed =
        creatingStage === CreatingStage.CHECKING ||
        creatingStage === CreatingStage.WAITING_APPROVAL;

      if (!isApproved || isCreateOnSCFired || !hasUserConfirmed) return;

      // For BNB chain, lixiBagAddress is required
      // For Solana, we can use lixiBagAddress from config or a default value
      if (currentChain !== 'solana' && !lixiBagAddress) return;

      setIsCreateOnSCFired(() => true);

      const { amountOnItem, numberItem, currency: extraToken } = fieldState;
      try {
        let extractDecimals: number | undefined;
        if (extraToken !== ADDRESS_ZERO) {
          extractDecimals = await getTokenDecimalsViaChainManager(extraToken);
        }

        const amountOnItemWei = parseUnits(
          amountOnItem.toString(),
          extractDecimals,
        ).toString();

        dispatch(setCreatingStage(CreatingStage.WAITING_USER));

        // For Solana, use lixiBagAddress from config or fallback to programId
        const campaignAddress =
          currentChain === 'solana'
            ? lixiBagAddress || chainConfig.programId || SOLANA_PROGRAM_ID
            : lixiBagAddress;

        // For Solana, receivers array must have length <= numMint (per Rust program validation)
        // Rust program does NOT allow duplicate receivers
        // If all tickets have the same receiver, send only one receiver address
        const walletAddress = wallet?.address;
        if (!walletAddress) {
          throw new Error('Wallet address is required');
        }

        // Create receivers array
        // Note: Rust program requires receivers.len() <= num_tickets and no duplicates
        // For now, if all tickets go to same receiver, send only one receiver
        // TODO: In future, allow user to specify different receivers per ticket
        const receivers = currentChain === 'solana'
          ? [walletAddress] // Solana: send single receiver (no duplicates allowed)
          : Array(numberItem).fill(walletAddress); // BNB: can have duplicates

        console.log('[CreateCampaign] Starting campaign creation...', {
          numberItem,
          extraToken,
          amountOnItemWei,
          campaignAddress,
          receiversCount: receivers.length,
        });

        // This will trigger wallet popup for user to confirm transaction
        const result = await createCampaignViaChainManager(
          numberItem,
          extraToken,
          amountOnItemWei,
          receivers,
          (numberItem * 0.01 * 2) as number,
          campaignAddress,
        );

        console.log('[CreateCampaign] Campaign creation result:', result);

        if (result?.success === false) {
          throw new Error(result.error || 'Failed to create campaign');
        }

        // Transaction succeeded
        // For Solana: save blockhashHash and creatorWalletAddress to Redux store for later use
        if (currentChain === 'solana' && result?.blockhashHash && result?.creatorWalletAddress) {
          dispatch(setBlockhashHash(result.blockhashHash));
          dispatch(setCreatorWalletAddress(result.creatorWalletAddress));
          console.log('[CreateCampaign] Saved Solana campaign data:', {
            blockhashHash: result.blockhashHash,
            creatorWalletAddress: result.creatorWalletAddress,
          });
        }

        // For BNB: wait for MintLixi event (handled by useMintLixi hook)
        // For Solana: useMintLixi hook will query on-chain data when CREATING_ON_SC stage
        dispatch(setCreatingStage(CreatingStage.CREATING_ON_SC));
      } catch (error) {
        dispatch(setCreatingStage(CreatingStage.IDLE));
        setIsCreateOnSCFired(false); // Reset to allow retry
        console.log(error);

        // Check if it's a time-related error
        let errorMessage = 'Oops, có gì đó hoạt động chưa đúng rồi';
        if (
          error instanceof Error &&
          error.message.includes('Campaign has expired')
        ) {
          // Extract time information from error message if available
          const timeMatch = error.message.match(/(\d+)h (\d+)m ago/);
          if (timeMatch) {
            const hours = timeMatch[1];
            const minutes = timeMatch[2];
            errorMessage =
              `Chiến dịch đã hết hạn từ ${hours} giờ ${minutes} phút trước. ` +
              `Vui lòng liên hệ admin để gia hạn hoặc tạo chiến dịch mới.`;
          } else {
            errorMessage =
              'Chiến dịch đã hết hạn. Vui lòng liên hệ admin để gia hạn hoặc tạo chiến dịch mới.';
          }
        } else if (
          error instanceof Error &&
          error.message.includes('out of time')
        ) {
          errorMessage = 'Chiến dịch đã hết hạn. Vui lòng tạo chiến dịch mới.';
        }

        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
        });
      }
    })();
  }, [
    isApproved,
    lixiBagAddress,
    fieldState,
    dispatch,
    toast,
    currentChain,
    chainConfig,
    creatingStage,
    isCreateOnSCFired,
  ]);

  useEffect(() => {
    (async () => {
      if (!isCreateConfirmed) return;

      setIsCreateConfirmed(false);
      dispatch(setCreatingStage(CreatingStage.CHECKING));

      // Check if token needs approval (not native token)
      const needsApproval =
        fieldState.currency &&
        fieldState.currency !== ADDRESS_ZERO &&
        fieldState.currency !== '';

      if (needsApproval) {
        // For SPL tokens, check approval status first
        await checkApproval();

        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 200));

        // Check approval status again - if still not approved, trigger approval
        // Note: We need to check the actual approval status here
        // Since checkApproval is async and updates state, we'll check via adapter directly
        const adapter = chainManager.getCurrentAdapter();
        if (adapter?.checkTokenApproval) {
          const approvalResult = await adapter.checkTokenApproval({
            tokenAddress: fieldState.currency,
            spender: spenderAddress,
            owner: wallet?.address,
          });

          if (!approvalResult.approved) {
            console.log('[Payment] Token not approved, triggering approval...');
            dispatch(setCreatingStage(CreatingStage.WAITING_APPROVAL));

            try {
              // Call approve function - this will trigger wallet approval
              await approve();

              // Wait for approval to be confirmed
              await new Promise(resolve => setTimeout(resolve, 2000));

              // Re-check approval
              await checkApproval();
            } catch (error) {
              // User rejected approval or error occurred
              console.error('[Payment] Approval failed:', error);
              dispatch(setCreatingStage(CreatingStage.IDLE));
              
              toast({
                title: 'Error',
                description: error instanceof Error 
                  ? error.message 
                  : 'Không thể phê duyệt token. Vui lòng thử lại.',
                status: 'error',
              });
              return;
            }
          } else {
            console.log('[Payment] Token already approved');
          }
        }
      } else {
        // For native tokens (SOL), approval is always true
        await checkApproval();
      }
    })();
  }, [
    isCreateConfirmed,
    checkApproval,
    approve,
    fieldState.currency,
    spenderAddress,
    chainManager,
    wallet?.address,
    dispatch,
    toast,
  ]);

  // Handle approval when token is not approved yet
  // This effect runs after checkApproval updates isApproved state
  useEffect(() => {
    (async () => {
      // Only proceed if we're in CHECKING stage and token is not approved
      // Skip if already approving or if it's a native token (no approval needed)
      if (
        creatingStage !== CreatingStage.CHECKING ||
        isApproved ||
        isApproving ||
        fieldState.currency === ADDRESS_ZERO ||
        !fieldState.currency
      ) {
        return;
      }

      console.log('[Approval] Starting approval process', {
        stage: creatingStage,
        isApproved,
        isApproving,
        currency: fieldState.currency,
      });

      try {
        // Set stage to WAITING_APPROVAL before starting approval
        dispatch(setCreatingStage(CreatingStage.WAITING_APPROVAL));

        console.log('[Approval] Calling approve() to trigger wallet...');

        // Call approve function - this will trigger wallet approval
        await approve();

        console.log('[Approval] Approval transaction sent, waiting for confirmation...');

        // Wait a bit for transaction to be confirmed
        await new Promise(resolve => setTimeout(resolve, 2000));

        // After approval, checkApproval will be called automatically via event listener
        // or we can manually check it
        await checkApproval();

        console.log('[Approval] Approval check completed');
      } catch (error) {
        // User rejected approval or error occurred
        console.error('[Approval] Approval failed:', error);
        dispatch(setCreatingStage(CreatingStage.IDLE));
        
        toast({
          title: 'Error',
          description: error instanceof Error 
            ? error.message 
            : 'Không thể phê duyệt token. Vui lòng thử lại.',
          status: 'error',
        });
      }
    })();
  }, [
    creatingStage,
    isApproved,
    isApproving,
    fieldState.currency,
    approve,
    checkApproval,
    dispatch,
    toast,
  ]);

  // Memoize onTokenChange callback to prevent infinite loops in SolanaExtraTokenForm
  const handleTokenChange = useCallback(
    (token: any, amount: number) => {
      // Update Redux state when token changes
      dispatch(
        setFields({
          currency: token?.mint?.toString() || ADDRESS_ZERO,
          amountOnItem: amount,
        }),
      );
    },
    [dispatch],
  );

  return (
    <Box color="white" paddingBottom="160px">
      <Heading
        fontSize="84px"
        fontFamily="headingFont"
        textAlign="center"
        marginBottom="48px"
      >
        Tạo Lì Xì
      </Heading>
      {lixiBagAddress ? (
        <Box mb={6} maxWidth="64rem" mx="auto">
          <CampaignTimeStatus lixiBagAddress={lixiBagAddress} />
        </Box>
      ) : null}
      <Flex
        justifyContent="space-around"
        flexDirection={{ base: 'column', md: 'row' }}
        maxWidth="64rem"
        mx="auto"
      >
        <Box
          width={{ base: '100%', md: '50%' }}
          pl={{ base: '1rem', md: '1.5rem' }}
          pr={{ base: '1rem', md: '1.5rem' }}
        >
          <FormTitle step={step} />
          <Box marginTop="69px">
            <FormControl>
              {step === 1 && <GeneralForm nextStep={nextStep} />}
              {step === 2 && <Amount nextStep={nextStep} goBack={goBack} />}
              {step === 3 &&
                (currentChain === 'solana' ? (
                  <SolanaExtraTokenForm
                    goBack={goBack}
                    onCreateCampaign={onOpen}
                    onTokenChange={handleTokenChange}
                  />
                ) : (
                  <ExtraTokenForm goBack={goBack} onCreateCampaign={onOpen} />
                ))}
            </FormControl>
          </Box>
        </Box>
        <Box
          width={{ base: '100%', md: '50%' }}
          pl={{ base: '1rem', md: '1.5rem' }}
          pr={{ base: '1rem', md: '1.5rem' }}
        >
          <Box
            width={{ base: '320px', lg: '480px' }}
            marginX="auto"
            position="sticky"
            top="4.5rem"
          >
            <Image
              marginX={{ base: 'auto', lg: '0' }}
              src={previewLixi}
              marginTop="4.5rem"
            />
            {fileUri ? (
              <Image
                position="absolute"
                transform="translateY(-4.5rem)"
                width={{ base: '70px', lg: '110px' }}
                borderRadius="50%"
                top={{ base: '301px', lg: '414px' }}
                left={{ base: '61px', lg: '88px' }}
                src={fileUri}
              />
            ) : null}
            <Box
              position="absolute"
              width="270px"
              top={{ base: '290px', lg: '455px' }}
              right={{ base: '-44px', lg: '5px' }}
              fontFamily="hashTagFont"
              fontSize={{
                base: fieldState.hashTag?.length < 11 ? '20px' : '15px',
                lg: fieldState.hashTag?.length < 11 ? '25px' : '20px',
              }}
              transform="rotate(-90deg) translateX(4.5rem)"
              color="#DAA74B"
            >
              {fieldState.hashTag ? fieldState.hashTag : ''}
            </Box>
          </Box>
        </Box>
      </Flex>

      {/** Warning Modal */}
      <EnvelopeWarningModal
        isOpen={isOpen}
        onClose={onClose}
        onOpen={() => {
          setIsCreateConfirmed(true);
          onClose();
        }}
      />

      {/** Creating modal */}
      <EnvelopeCreatingModal />
    </Box>
  );
};
